// Rehome preserves CDT order by sorting every lane against a declared order built once.
import { runOffsetsOf } from "../spineInference";
import { itemsWithLayout } from "../verticalBlockingFix";
import {
  GeneratedLayout,
  PATH_SEPARATOR,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  effectiveAttrs,
} from "../studioTypes";
import { diagnoseColumns } from "./columnDiagnosis";
import { DEFAULT_HIERARCHY_MOVE_RULES, HierarchyMoveRules } from "./hierarchyMoveRules";
import { applyBlockOrder, cloneLayout } from "./layoutOps";
import { rebuildColumn } from "./rebuildColumn";
import { vacateShelf } from "./vacateShelf";

const OUTSIDE_PREFIX = "__outside__:";

export interface RehomeConcession {
  kind: "stranded" | "not-banded";
  detail: string;
}

export interface RehomeRequest {
  scopeKey: string;
  attr: string;
  value: string;
  destIndex: number;
}

export interface RehomeResult {
  layout: GeneratedLayout;
  /** Scope block values, declared order (the contract after the move). */
  declaredOrder: readonly string[];
  /** Scope block values in the previous reading order (the baseline). */
  previousOrder: readonly string[];
  travelling: readonly string[];
  housings: readonly { orphan: string; host: readonly string[] }[];
  concessions: readonly RehomeConcession[];
  /** Block identity over the WHOLE plan, for the lane-order audit. */
  blockKeyOfItem: (item: StudioItem) => string;
  /** Per-run rank for OUTSIDE blocks only, which construction holds fixed; the
   * audit verifies that rather than trusting it. */
  outsideRankOf: (run: number, blockKey: string) => number | undefined;
  /** The generator's key for the moved scope (inert levels skipped). */
  scopeGeneratorKey: string;
}

/**
 * Resolve a scope key to its item set and the GENERATOR's path key. The tree
 * keeps inert level values in its paths and the generator skips them, so this
 * tolerates both by consuming components as a subsequence.
 * TODO(scope-keys): third site of this dual-key resolution — centralize it.
 */
export const resolveScope = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  scopeKey: string,
  attr: string,
): { scopeItems: StudioItem[]; scopeGeneratorKey: string; levelIndex: number } | null => {
  const levelIndex = draft.spine.findIndex((level) => level.attr === attr);
  if (levelIndex < 0) return null;
  const pending = scopeKey ? scopeKey.split(PATH_SEPARATOR) : [];
  let cursor = 0;
  let current = [...items];
  const generatorPath: string[] = [];
  for (let level = 0; level < levelIndex; level++) {
    const attrKey = draft.spine[level].attr;
    const values = new Set(
      current.map((item) => String(effectiveAttrs(item, draft)[attrKey] ?? "")),
    );
    if (values.size >= 2) {
      const component = pending[cursor];
      if (component === undefined || !values.has(component)) return null;
      cursor += 1;
      generatorPath.push(component);
      current = current.filter(
        (item) => String(effectiveAttrs(item, draft)[attrKey] ?? "") === component,
      );
    } else if (pending[cursor] !== undefined && values.has(pending[cursor])) {
      cursor += 1; // inert level value present in a tree-convention key
    }
  }
  if (cursor !== pending.length) return null;
  return { scopeItems: current, scopeGeneratorKey: generatorPath.join(PATH_SEPARATOR), levelIndex };
};

export const rehomeBlock = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  displayed: GeneratedLayout,
  request: RehomeRequest,
  rules: HierarchyMoveRules = DEFAULT_HIERARCHY_MOVE_RULES,
): RehomeResult | null => {
  const scope = resolveScope(items, draft, request.scopeKey, request.attr);
  if (!scope) return null;
  const scopeSkus = new Set(scope.scopeItems.map((item) => item.skuId));
  const bySku = new Map(items.map((item) => [item.skuId, item]));
  const attrsOf = (skuId: string): Record<string, string | null> => {
    const item = bySku.get(skuId);
    return item ? effectiveAttrs(item, draft) : {};
  };
  const slabDepth = Math.max(1, Math.min(scope.levelIndex + 1, draft.spine.length));
  const blockKeyOfItem = (item: StudioItem): string => {
    if (scopeSkus.has(item.skuId)) {
      return String(effectiveAttrs(item, draft)[request.attr] ?? "");
    }
    const attrs = effectiveAttrs(item, draft);
    return (
      OUTSIDE_PREFIX +
      draft.spine
        .slice(0, slabDepth)
        .map((level) => attrs[level.attr] ?? "")
        .join(PATH_SEPARATOR)
    );
  };
  const blockKeyOf = (skuId: string): string => {
    const item = bySku.get(skuId);
    return item ? blockKeyOfItem(item) : OUTSIDE_PREFIX;
  };
  if (!scope.scopeItems.some((item) => blockKeyOfItem(item) === request.value)) return null;

  // Reading order of every block: globally (linearized x) and per run.
  const offsets = runOffsetsOf(runs);
  const projected = itemsWithLayout(items, displayed);
  const x0OfBlock = new Map<string, number>();
  const x0PerRun = new Map<number, Map<string, number>>();
  for (const item of projected) {
    const key = blockKeyOfItem(item);
    for (const placement of item.placements) {
      const x = placement.xCm + (offsets[placement.run] ?? 0);
      const known = x0OfBlock.get(key);
      if (known === undefined || x < known) x0OfBlock.set(key, x);
      const perRun = x0PerRun.get(placement.run) ?? new Map<string, number>();
      const knownRun = perRun.get(key);
      if (knownRun === undefined || placement.xCm < knownRun) perRun.set(key, placement.xCm);
      x0PerRun.set(placement.run, perRun);
    }
  }
  const globalFlow = [...x0OfBlock.entries()].sort((a, b) => a[1] - b[1]).map(([key]) => key);
  const previousOrder = globalFlow.filter((key) => !key.startsWith(OUTSIDE_PREFIX));
  if (!previousOrder.includes(request.value)) return null;
  const runSequences = new Map<number, string[]>();
  for (const [runId, perRun] of x0PerRun) {
    runSequences.set(
      runId,
      [...perRun.entries()].sort((a, b) => a[1] - b[1]).map(([key]) => key),
    );
  }

  // The mover's run and column, at the moved level's granularity.
  const moverItems = scope.scopeItems.filter((item) => blockKeyOfItem(item) === request.value);
  const runVotes = new Map<number, number>();
  for (const item of moverItems) runVotes.set(item.run, (runVotes.get(item.run) ?? 0) + 1);
  const moverRunId = [...runVotes.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? runs[0].run;
  const moverRun = runs.find((run) => run.run === moverRunId) ?? runs[0];
  const shelfCount = moverRun.shelves.length;
  const diagnosis = diagnoseColumns(displayed, items, runs, draft, { blockKeyOf: blockKeyOfItem });
  const startColumn = diagnosis.cols.find((col) => col.members.includes(request.value));

  // A block with fewer facings than shelves can never be a column of its
  // own; moving it alone shreds its stack — the whole stack travels.
  const moverFacings = moverItems.reduce((sum, item) => sum + item.facings, 0);
  const travelling =
    moverFacings < shelfCount && startColumn && startColumn.members.length > 1
      ? startColumn.members.filter((member) => previousOrder.includes(member))
      : [request.value];
  const prevRank = new Map(previousOrder.map((key, index) => [key, index]));
  const group = [...travelling].sort((a, b) => (prevRank.get(a) ?? 0) - (prevRank.get(b) ?? 0));
  const rest = previousOrder.filter((key) => !group.includes(key));

  // A mover lands BETWEEN columns, never inside one: snap the destination
  // to the nearest column boundary, expressed as positions in `rest`.
  const bounds = new Set<number>([0, rest.length]);
  for (const col of diagnosis.cols) {
    const last = col.members
      .filter((member) => rest.includes(member))
      .reduce((max, member) => Math.max(max, rest.indexOf(member)), -1);
    if (last >= 0) bounds.add(last + 1);
  }
  const wantAt = Math.max(0, Math.min(rest.length, request.destIndex));
  const at = [...bounds].reduce(
    (best, bound) => (Math.abs(bound - wantAt) < Math.abs(best - wantAt) ? bound : best),
    0,
  );
  const declaredOrder = [...rest];
  declaredOrder.splice(at, 0, ...group);

  // Per-run ranks: outside blocks keep their slots and each run's SCOPE slots are
  // re-filled in declared order, so a cross-run block stays legal in every run.
  const declaredRank = new Map(declaredOrder.map((key, index) => [key, index]));
  const rankPerRun = new Map<number, Map<string, number>>();
  for (const [runId, sequence] of runSequences) {
    const scopeInRun = sequence
      .filter((key) => declaredRank.has(key))
      .sort((a, b) => (declaredRank.get(a) ?? 0) - (declaredRank.get(b) ?? 0));
    let slot = 0;
    const permuted = sequence.map((key) => (declaredRank.has(key) ? scopeInRun[slot++] : key));
    rankPerRun.set(runId, new Map(permuted.map((key, index) => [key, index])));
  }
  const rankOf = (runId: number, key: string): number =>
    rankPerRun.get(runId)?.get(key) ?? Number.MAX_SAFE_INTEGER;
  const outsideRankOf = (runId: number, key: string): number | undefined =>
    key.startsWith(OUTSIDE_PREFIX) ? rankPerRun.get(runId)?.get(key) : undefined;

  let layout = cloneLayout(displayed);
  const reorder = () => applyBlockOrder(layout, items, blockKeyOf, rankOf);
  reorder();

  const concessions: RehomeConcession[] = [];
  const housings: { orphan: string; host: readonly string[] }[] = [];
  const housed = (key: string): boolean => {
    const now = diagnoseColumns(layout, items, runs, draft, { blockKeyOf: blockKeyOfItem });
    const col = now.cols.find((candidate) => candidate.members.includes(key));
    return Boolean(col?.fullHeight);
  };
  const childAttrOf = (key: string): string | null => {
    const own = scope.scopeItems.filter((item) => blockKeyOfItem(item) === key);
    for (let level = scope.levelIndex + 1; level < draft.spine.length; level++) {
      const attrKey = draft.spine[level].attr;
      const values = new Set(own.map((item) => String(effectiveAttrs(item, draft)[attrKey] ?? "")));
      if (values.size >= 2) return attrKey;
    }
    return null;
  };

  // The mover's blocking, by the measured house rule.
  const verticalBlock = moverFacings / shelfCount >= rules.minFacingsPerShelf;
  const moverShelves = new Set(
    moverItems.flatMap((item) =>
      (layout.placements[item.skuId]?.segments ?? [])
        .filter((segment) => segment.run === moverRunId)
        .map((segment) => segment.shelf),
    ),
  );
  if (travelling.length === 1) {
    if (verticalBlock && moverShelves.size < shelfCount) {
      // A vertical block stands the full height, levelled BY ITS OWN
      // child level so child bands stay contiguous and non-overlapping.
      rebuildColumn(layout, items, attrsOf, {
        group: [request.value],
        blockKeyOf,
        run: moverRun,
        childAttrOf,
      });
      reorder();
    } else if (!verticalBlock) {
      // A horizontal block bands with its immediate CDT neighbour —
      // narrowest window first, first feasible wins.
      const moverAt = declaredOrder.indexOf(request.value);
      const windows: string[][] = [];
      if (moverAt > 0) windows.push([declaredOrder[moverAt - 1], request.value]);
      if (moverAt < declaredOrder.length - 1) {
        windows.push([request.value, declaredOrder[moverAt + 1]]);
      }
      let banded = false;
      for (const window of windows) {
        const snapshot = cloneLayout(layout);
        const bands = rebuildColumn(layout, items, attrsOf, {
          group: window,
          blockKeyOf,
          run: moverRun,
          childAttrOf,
        });
        reorder();
        if (bands && housed(request.value)) {
          banded = true;
          housings.push({
            orphan: request.value,
            host: window.filter((member) => member !== request.value),
          });
          break;
        }
        layout = snapshot;
      }
      if (!banded && !housed(request.value)) {
        concessions.push({
          kind: "not-banded",
          detail: `no neighbouring block could carry "${request.value}" as a band`,
        });
      }
    }
  }

  // Orphans: the blocks left behind when the mover leaves its column.
  const orphans = (startColumn?.members ?? []).filter(
    (member) => !group.includes(member) && previousOrder.includes(member),
  );
  if (orphans.length >= 2) {
    // Rebuild the remainder as ONE column; one orphan at a time strands them.
    const snapshot = cloneLayout(layout);
    const ordered = [...orphans].sort(
      (a, b) => (declaredRank.get(a) ?? 0) - (declaredRank.get(b) ?? 0),
    );
    const bands = rebuildColumn(layout, items, attrsOf, {
      group: ordered,
      blockKeyOf,
      run: moverRun,
      childAttrOf,
    });
    reorder();
    if (bands && ordered.every((orphan) => housed(orphan))) {
      housings.push({ orphan: ordered.join(" + "), host: [] });
    } else {
      layout = snapshot;
      reorder();
    }
  }
  for (const orphan of orphans) {
    if (housed(orphan)) continue;
    // Cheapest first: vacate — free the shelves the orphan's column needs
    // from a former column-mate; nobody moves in x.
    {
      const snapshot = cloneLayout(layout);
      const now = diagnoseColumns(layout, items, runs, draft, { blockKeyOf: blockKeyOfItem });
      const col = now.cols.find((candidate) => candidate.members.includes(orphan));
      const covered = new Set(col?.shelves ?? []);
      const needed = moverRun.shelves
        .map((shelf) => shelf.rank)
        .filter((rank) => !covered.has(rank));
      const moved = vacateShelf(layout, items, {
        orphanKey: orphan,
        neededShelves: needed,
        blockKeyOf,
        run: moverRun,
      });
      reorder();
      if (moved && housed(orphan)) {
        housings.push({ orphan, host: [...new Set(moved)] });
        continue;
      }
      layout = snapshot;
      reorder();
    }
    // Then rebuild with the orphan's OWN CDT neighbours; it never moves in x.
    const orphanAt = declaredOrder.indexOf(orphan);
    const windows: string[][] = [];
    if (orphanAt > 0) windows.push([declaredOrder[orphanAt - 1], orphan]);
    if (orphanAt < declaredOrder.length - 1) windows.push([orphan, declaredOrder[orphanAt + 1]]);
    if (orphanAt > 1) windows.push(declaredOrder.slice(orphanAt - 2, orphanAt + 1));
    if (orphanAt < declaredOrder.length - 2) {
      windows.push(declaredOrder.slice(orphanAt, orphanAt + 3));
    }
    let done = false;
    for (const window of windows) {
      const snapshot = cloneLayout(layout);
      const bands = rebuildColumn(layout, items, attrsOf, {
        group: window,
        blockKeyOf,
        run: moverRun,
        childAttrOf,
      });
      reorder();
      if (bands && housed(orphan)) {
        housings.push({ orphan, host: window.filter((member) => member !== orphan) });
        done = true;
        break;
      }
      layout = snapshot;
      reorder();
    }
    if (!done) {
      concessions.push({
        kind: "stranded",
        detail: `"${orphan}" ended outside a full-height column`,
      });
    }
  }

  reorder();
  return {
    layout,
    declaredOrder,
    previousOrder,
    travelling: group,
    housings,
    concessions,
    blockKeyOfItem,
    outsideRankOf,
    scopeGeneratorKey: scope.scopeGeneratorKey,
  };
};
